import { Alert, AlertIcon, Box } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import {
  CallOffRefusalDto,
  SourcingEventStatus,
  type SourcingEventDto,
  useGetFrameworkAgreementQuery,
} from "../../autogen/bff-api";

const frameworkAgreementIdOf = (event: SourcingEventDto): string | null =>
  event.eventContext.type === "MiniCompetition" ? event.eventContext.frameworkAgreementId : null;

const callOffStillAhead = (event: SourcingEventDto): boolean =>
  event.status !== SourcingEventStatus.Cancelled &&
  event.status !== SourcingEventStatus.Completed &&
  (event.award?.contractIds ?? []).length === 0;

const useFrameworkRefusal = (event: SourcingEventDto, appliesToEvent: boolean) => {
  const frameworkAgreementId = frameworkAgreementIdOf(event);
  const applies = frameworkAgreementId !== null && appliesToEvent;
  const link = event.frameworkAgreementLink;
  const readsTheAgreement = applies && frameworkAgreementId !== null && link?.mayOpen !== false;
  const { data: framework } = useGetFrameworkAgreementQuery(
    readsTheAgreement ? { frameworkAgreementId } : skipToken,
  );
  return { applies, refusal: readsTheAgreement ? framework?.callOffRefusal : link?.callOffRefusal };
};

const useCallOffRefusal = (event: SourcingEventDto) => useFrameworkRefusal(event, callOffStillAhead(event));

const useRefusalReason = () => {
  const { t } = useTranslation();
  return (refusal: CallOffRefusalDto | null | undefined): string | null => {
    switch (refusal) {
      case CallOffRefusalDto.NotActive:
        return t("The framework agreement is no longer active.");
      case CallOffRefusalDto.TermEnded:
        return t("The framework agreement's term has ended.");
      case CallOffRefusalDto.TermNotStarted:
        return t("The framework agreement's term has not started yet.");
      default:
        return null;
    }
  };
};

export const useCopyRefusal = (event: SourcingEventDto): string | null => {
  const { t } = useTranslation();
  const reasonFor = useRefusalReason();
  const { applies, refusal } = useFrameworkRefusal(event, true);
  const reason = applies ? reasonFor(refusal) : null;
  return reason && t("{{reason}} No new mini-competition can be made under it, so this one can't be copied.", { reason });
};

export const useAwardMayBeWithdrawn = (event: SourcingEventDto): boolean => {
  const { applies, refusal } = useCallOffRefusal(event);
  return applies && event.status === SourcingEventStatus.Awarded && refusal === CallOffRefusalDto.NotActive;
};

const UNAWARDED = new Set<SourcingEventStatus>([
  SourcingEventStatus.Published,
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
]);

export const useUnawardedMiniCompetitionRefused = (event: SourcingEventDto): boolean => {
  const { applies, refusal } = useCallOffRefusal(event);
  return applies && UNAWARDED.has(event.status) && refusal != null;
};

export const useFrameworkNoLongerActive = (event: SourcingEventDto): string | null => {
  const { t } = useTranslation();
  const reasonFor = useRefusalReason();
  const { applies, refusal } = useCallOffRefusal(event);
  if (!applies) return null;

  if (event.status === SourcingEventStatus.Awarded) {
    return refusal === CallOffRefusalDto.NotActive
      ? t(
          "The framework agreement is no longer active, so no call-off can be made from this award. Cancel the mini-competition to tell every participant that no contract follows.",
        )
      : null;
  }

  const reason = reasonFor(refusal);
  return reason && t("{{reason}} This mini-competition can't be published, awarded or turned into a call-off.", { reason });
};

export const FrameworkNoLongerActive = ({ event }: { event: SourcingEventDto }) => {
  const message = useFrameworkNoLongerActive(event);
  if (!message) return null;

  return (
    <Box mt="4">
      <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
        <AlertIcon />
        {message}
      </Alert>
    </Box>
  );
};
